export type FaqCategory =
  | "Pricing"
  | "Booking"
  | "Studio Features"
  | "Equipment"
  | "Logistics"
  | "Photo & Video";

export const FAQ_CATEGORIES: FaqCategory[] = [
  "Pricing",
  "Booking",
  "Studio Features",
  "Equipment",
  "Logistics",
  "Photo & Video",
];

export type FaqItem = {
  q: string;
  a: string;
  category: FaqCategory;
};

/** URL-safe slug for deep-linking to a FAQ category or question. */
export const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60);

export const faqAnchorId = (q: string) => `q-${slugify(q)}`;
export const faqCategoryAnchorId = (c: FaqCategory) => `cat-${slugify(c)}`;

export const FAQS: FaqItem[] = [
  // Pricing
  {
    category: "Pricing",
    q: "How much does it cost to rent a photo studio in Columbus, GA?",
    a: "Hourly rentals start at our base studio rate and vary by space (Studio A, Studio B, or the Beauty Station). The total depends on the studio, duration, and any add-ons such as backdrops or the beauty station. See the Rates page for the current breakdown.",
  },
  {
    category: "Pricing",
    q: "Do you charge by the hour or in packages?",
    a: "We rent by the hour with a one-hour minimum. We also publish discounted multi-hour packages on the Rates page so longer shoots get a better effective rate.",
  },
  {
    category: "Pricing",
    q: "Is payment required upfront?",
    a: "Yes. All studio bookings require 100% payment at checkout through Stripe. This guarantees your slot is locked the moment the booking is created.",
  },
  {
    category: "Pricing",
    q: "What is your refund and cancellation policy?",
    a: "Cancellations made at least 48 hours before your booking are eligible for a full refund or reschedule. Inside the 48-hour window the deposit is non-refundable but credit toward a future booking may be offered at our discretion. Full policy is on the Studio Rules page.",
  },

  // Booking
  {
    category: "Booking",
    q: "How do I book the studio?",
    a: "Use the Rent Studio button to open the booking flow. Choose your studio, pick a date and time, add any extras, enter your contact details, then pay through Stripe. You'll receive an email confirmation immediately.",
  },
  {
    category: "Booking",
    q: "What is the minimum booking time?",
    a: "The minimum is one hour. You can extend in one-hour increments at checkout, and longer blocks unlock package pricing.",
  },
  {
    category: "Booking",
    q: "Can I book same-day?",
    a: "Yes — if a slot is open on the calendar you can book it the same day. Availability is updated in real time so anything you see on the booking page is bookable.",
  },
  {
    category: "Booking",
    q: "Will I get a confirmation email?",
    a: "Yes. As soon as Stripe confirms your payment we send a booking confirmation email with the studio address, date, time, and a calendar invite. A reminder is sent the day before your shoot.",
  },
  {
    category: "Booking",
    q: "Can I reschedule my booking?",
    a: "Yes. Email services@bookmystudiospace.com or call 706.478.7640 at least 48 hours before your shoot and we will move it to any open slot at no extra cost.",
  },

  // Studio Features
  {
    category: "Studio Features",
    q: "What's included in the studio rental?",
    a: "Every booking includes access to our lighting kits, seamless paper backdrops, the cyclorama wall, Wi-Fi, climate control, a private restroom, and a client lounge area. Specialty backdrops and the beauty station can be added at checkout.",
  },
  {
    category: "Studio Features",
    q: "Do you have a cyclorama (cyc) wall?",
    a: "Yes. Studio A features a full cyclorama wall ideal for video, fashion, and product shoots that need a seamless infinity background.",
  },
  {
    category: "Studio Features",
    q: "Is there a makeup or beauty station?",
    a: "Yes. The Beauty Station is a dedicated hair and makeup room with ring lights, a styling chair, and mirrors. It can be booked on its own or added to any studio rental.",
  },
  {
    category: "Studio Features",
    q: "Is the studio Wi-Fi fast enough for tethering and uploads?",
    a: "Yes. We provide high-speed Wi-Fi suitable for tethered capture, live streaming, and same-day uploads.",
  },
  {
    category: "Studio Features",
    q: "Is the space sound-treated for video and podcasts?",
    a: "Yes. The studios are acoustically treated to keep ambient noise and echo low, making them well-suited for interviews, podcasts, and dialogue-driven video.",
  },

  // Equipment
  {
    category: "Equipment",
    q: "What lighting equipment is included?",
    a: "Continuous and strobe lighting kits with softboxes, octaboxes, umbrellas, V-flats, reflectors, and C-stands are included with every booking. Modifiers and grip are stored on-site and ready to go.",
  },
  {
    category: "Equipment",
    q: "Do you provide cameras and lenses?",
    a: "No — cameras and lenses are not included. Bring your own kit. We can recommend local rental partners if you need additional bodies or glass.",
  },
  {
    category: "Equipment",
    q: "Can I bring my own equipment?",
    a: "Absolutely. You can bring any cameras, lenses, lighting, modifiers, or props you need. Just give us a heads-up on large gear so we can plan load-in.",
  },
  {
    category: "Equipment",
    q: "What backdrop colors do you have?",
    a: "We stock multiple seamless paper colors (white, black, grey, and seasonal colors) plus muslin and printed backdrops. Pick the ones you want as add-ons in the booking flow.",
  },
  {
    category: "Equipment",
    q: "Do you have props, furniture, or styling pieces?",
    a: "Yes. We have a rotating selection of props, stools, and small furniture. Throne chairs and specialty rentals are listed in the Shop.",
  },

  // Logistics
  {
    category: "Logistics",
    q: "Where is the studio located?",
    a: "MyStudio is at 2300 Whittlesey Rd Ste E, Columbus, GA 31909. Full directions are included in your booking confirmation email.",
  },
  {
    category: "Logistics",
    q: "Is there parking on site?",
    a: "Yes. We have free on-site parking with easy load-in access right next to the studio entrance.",
  },
  {
    category: "Logistics",
    q: "How do I check in?",
    a: "Your confirmation email includes the entry instructions. For most bookings, the studio is unlocked at your start time and a team member is available on-site or by phone.",
  },
  {
    category: "Logistics",
    q: "Can I arrive early to set up?",
    a: "Your booked time includes your setup and breakdown. If you need extra prep time, add it to your booking so the slot is reserved. Arriving early when the previous client is still shooting is not permitted.",
  },
  {
    category: "Logistics",
    q: "Can I bring food, drinks, or alcohol?",
    a: "Light food and non-alcoholic drinks are welcome in the lounge area. Alcohol and open flames require prior approval. Cleanup is your responsibility — see Studio Rules for damage and cleaning policies.",
  },
  {
    category: "Logistics",
    q: "Are pets allowed in the studio?",
    a: "Pets are welcome for photo shoots when arranged in advance. Please mention pets in the booking notes so we can prep the space.",
  },

  // Photo & Video
  {
    category: "Photo & Video",
    q: "Can I shoot video in the studio?",
    a: "Yes. The studios are sized and lit for video production — music videos, brand content, interviews, and social shoots. The cyc wall in Studio A is ideal for clean infinity backgrounds.",
  },
  {
    category: "Photo & Video",
    q: "Do you allow drones or smoke machines?",
    a: "Small indoor drones and haze/fog machines are allowed with prior approval. Mention them in your booking notes so we can stage ventilation and clear the space.",
  },
  {
    category: "Photo & Video",
    q: "Can I host a workshop, event, or content day?",
    a: "Yes. We host workshops, brand content days, and small events. Email services@bookmystudiospace.com for multi-hour or full-day pricing.",
  },
  {
    category: "Photo & Video",
    q: "I'm a first-time renter — what should I know?",
    a: "Arrive a few minutes early, plan your setup inside your booked time, and ask us any questions ahead of time. We'll walk first-timers through the lights and gear at the start of the booking.",
  },
  {
    category: "Photo & Video",
    q: "Do you offer in-house photography or production services?",
    a: "Yes. If you need a photographer, videographer, editor, or post-production, see our Services pages. You can rent the studio only, or book a fully produced shoot with our team.",
  },
];

export const faqSchema = (items: FaqItem[] = FAQS) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  // Dedupe by question so every accordion answer is represented exactly once
  // in the FAQPage JSON-LD — Google penalises duplicate Question entries.
  mainEntity: Array.from(
    new Map(items.map((f) => [f.q.trim().toLowerCase(), f])).values(),
  ).map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

/** Pick a small set of high-intent FAQs for inline snippets (booking/checkout). */
export const bookingFaqSnippets: FaqItem[] = [
  FAQS.find((f) => f.q.startsWith("Is payment required"))!,
  FAQS.find((f) => f.q.startsWith("What is the minimum"))!,
  FAQS.find((f) => f.q.startsWith("What is your refund"))!,
  FAQS.find((f) => f.q.startsWith("What's included"))!,
  FAQS.find((f) => f.q.startsWith("Can I bring my own"))!,
  FAQS.find((f) => f.q.startsWith("Is there parking"))!,
];